import { Component, Inject } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { OTAJob } from '../ota-dashboard/ota-dashboard.component';
import { OtaService } from 'src/app/Core/services/ota.service';
import { HttpErrorResponse } from '@angular/common/http';
import { ToasterService } from 'src/app/Core/services/toaster.service';
import { finalize } from 'rxjs/operators';

export interface ExecutionReport {
  status: string;
  number_of_succeeded_things: number;
  number_of_in_progress_things: number;
  number_of_failed_things: number;
  number_of_queued_things: number;
  number_of_rejected_things: number;
  number_of_canceled_things: number;
  number_of_timed_out_things: number;
  number_of_removed_things: number;
}

export interface OtaDetailDialogResult {
  action: 'cancel' | 'delete';
}

@Component({
  selector: 'app-ota-detail',
  templateUrl: './ota-detail.component.html',
  styleUrls: ['./ota-detail.component.css']
})
export class OtaDetailComponent {
  isActionInProgress = false;

  executionReport: ExecutionReport = {
    status: 'IN_PROGRESS',
    number_of_succeeded_things: 0,
    number_of_in_progress_things: 0,
    number_of_failed_things: 0,
    number_of_queued_things: 0,
    number_of_rejected_things: 0,
    number_of_canceled_things: 0,
    number_of_timed_out_things: 0,
    number_of_removed_things: 0
  };

  basicDetailsExpanded = true;
  wifiFirmwareExpanded = true;
  mainFirmwareExpanded = true;
  configurationsExpanded = true;

  constructor(
    public dialogRef: MatDialogRef<OtaDetailComponent>,
    @Inject(MAT_DIALOG_DATA) public job: OTAJob,
    private otaService: OtaService,
    private toaster: ToasterService,
  ) { }

  ngOnInit(): void {
    this.fetchOTAJobProgress();
  }

  get isCancelledJob(): boolean {
    return this.job.status === 'CANCELLED';
  }

  fetchOTAJobProgress() {
    this.otaService.getOTAJobProgress(this.job.id).subscribe({
      next: (data: ExecutionReport) => {
        data.status = this.job.status; // Ensure status is updated from job data
        this.executionReport = data;
      },
      error: (error: any) => {
        console.error("Error fetching OTA job progress:", error);
        if (error instanceof HttpErrorResponse) {
          this.toaster.openDialogBox(error.error.message, false);
          this.close();
        }
      }
    });
  }

  close(result?: OtaDetailDialogResult): void {
    this.dialogRef.close(result);
  }

  // onActionButton(): void {
  //   if (this.actionButtonLabel === 'Cancel') {
  //     this.actionButtonLabel = 'Delete';
  //   } else {
  //     this.dialogRef.close({ action: 'delete', job: this.job });
  //   }
  // }

  onActionButton(): void {
    if (this.isActionInProgress) {
      return;
    }

    this.isActionInProgress = true;

    if (!this.isCancelledJob) {
      this.otaService.cancelOTAJob(this.job.id).pipe(
        finalize(() => {
          this.isActionInProgress = false;
        })
      ).subscribe({
        next: () => {
          this.toaster.openDialogBox('OTA job cancelled successfully', false);
          this.close({ action: 'cancel' });
        },
        error: (error: any) => {
          console.error("Error cancelling OTA job:", error);
          if (error instanceof HttpErrorResponse) {
            this.toaster.openDialogBox(error.error.message, false);
          }
        }
      });
    } else {
      this.otaService.deleteOTAJob(this.job.id).pipe(
        finalize(() => {
          this.isActionInProgress = false;
        })
      ).subscribe({
        next: () => {
          this.toaster.openDialogBox('OTA job deleted successfully', false);
          this.close({ action: 'delete' });
        },
        error: (error: any) => {
          console.error("Error deleting OTA job:", error);
          if (error instanceof HttpErrorResponse) {
            this.toaster.openDialogBox(error.error.message, false);
          }
        }
      });
    }
  }
  

  formatStatus(status: string): string {
    switch (status) {
      case 'IN_PROGRESS': return 'In Progress';
      case 'SUCCEEDED': return 'Success';
      case 'CANCELLED': return 'Cancelled';
      case 'REJECTED': return 'Rejected';
      case 'QUEUED': return 'Queued';
      case 'DELETED': return 'Deleted';
      default: return status;
    }
  }

  getStatusClass(status: string): string {
    switch (status) {
      case 'IN_PROGRESS': return 'status-in_progress';
      case 'SUCCEEDED': return 'status-succeeded';
      case 'CANCELLED': return 'status-cancelled';
      case 'REJECTED': return 'status-rejected';
      case 'QUEUED': return 'status-queued';
      case 'DELETED': return 'status-deleted';
      default: return '';
    }
  }

  formatPriority(priority: number | string): string {
    if (typeof priority === 'string') return priority;
    switch (priority) {
      case 1: return 'High';
      case 2: return 'Medium';
      case 3: return 'Low';
      default: return String(priority);
    }
  }

  getAbortCriteria(index: number) {
    return this.job?.abort_config?.criteriaList?.[index] ?? null;
  }

  formatRegions(regions: string | string[] | undefined | null): string {
    if (!regions) return '-';
    if (Array.isArray(regions)) return regions.join(', ');
    return regions;
  }
}

